/**
 * Compute point counts for each individual brick layer area (municipality, country, timezone)
 * Returns data for each individual region with its point count
 * 
 * @param pointData - GeoJSON FeatureCollection with point features
 * @param layerGeoJSON - GeoJSON FeatureCollection with brick layer features (municipalities, countries, timezones)
 * @param layerType - Type of brick layer: "municipalities" | "countries" | "timezones"
 * @returns Promise that resolves to array of region data with point counts
 */
import {
  featureBoundingBox,
  pointInBoundingBox,
  pointInGeoJSONFeature,
} from "./pointInPolygon";

export interface RegionData {
  regionId: string;
  pointCount: number;
  displayName?: string;
}

// Simple cache to avoid recomputing for the same data
const computationCache = new Map<string, RegionData[]>();
const CACHE_SIZE_LIMIT = 10; // Limit cache size to prevent memory issues

function getCacheKey(pointData: any, layerGeoJSON: any, layerType: string): string {
  // Create a simple hash from data lengths and a sample of IDs
  const pointCount = pointData?.features?.length || 0;
  const regionCount = layerGeoJSON?.features?.length || 0;
  const samplePointIds = pointData?.features?.slice(0, 5).map((f: any) => f.id || "").join(",") || "";
  const sampleRegionIds = layerGeoJSON?.features?.slice(0, 5).map((f: any, i: number) => 
    f.properties?.label_en || f.properties?.name || i
  ).join(",") || "";
  return `${layerType}-${pointCount}-${regionCount}-${samplePointIds}-${sampleRegionIds}`;
}

/**
 * Process regions in batches to avoid blocking the UI
 */
function processRegionsInBatches(
  layerFeatures: GeoJSON.Feature[],
  pointFeatures: any[],
  featureBBoxes: (([number, number, number, number] | null)[]),
  layerType: "municipalities" | "countries" | "timezones",
  batchSize: number = 10,
  onProgress?: (completed: number, total: number) => void
): Promise<RegionData[]> {
  return new Promise((resolve) => {
    const regions: RegionData[] = [];
    let currentIndex = 0;
    const total = layerFeatures.length;

    const processBatch = () => {
      const endIndex = Math.min(currentIndex + batchSize, total);
      
      for (let i = currentIndex; i < endIndex; i++) {
        const feature = layerFeatures[i];
        const regionId = `layer-${layerType}-${i}`;
        const bbox = featureBBoxes[i];

        // Find all points inside this feature
        let pointCount = 0;

        // Only process if we have a valid bounding box
        if (bbox) {
          for (const pointFeature of pointFeatures) {
            const pointCoords = pointFeature.geometry?.coordinates;
            
            // Validate point coordinates
            if (!pointCoords || !Array.isArray(pointCoords) || pointCoords.length < 2) {
              continue;
            }

            const coords = pointCoords as [number, number];
            
            // Validate coordinates are numbers
            if (typeof coords[0] !== "number" || typeof coords[1] !== "number" || 
                !isFinite(coords[0]) || !isFinite(coords[1])) {
              continue;
            }

            // Quick bounding box check first
            if (pointInBoundingBox(coords, bbox)) {
              // Only do expensive point-in-polygon check if point is in bounding box
              if (pointInGeoJSONFeature(coords, feature)) {
                pointCount++;
              }
            }
          }
        }

        // Extract display name from feature properties based on layer type
        const props = feature.properties || {};
        let displayName: string | undefined;
        
        if (layerType === "municipalities") {
          displayName = props.label_en;
        } else if (layerType === "countries" || layerType === "timezones") {
          displayName = props.name;
        }
        
        // Fallback to undefined if property not found
        if (!displayName || typeof displayName !== "string") {
          displayName = undefined;
        }

        regions.push({
          regionId,
          pointCount,
          displayName,
        });
      }

      currentIndex = endIndex;

      if (onProgress) {
        onProgress(currentIndex, total);
      }

      if (currentIndex < total) {
        // Use requestAnimationFrame for smooth UI updates, fallback to setTimeout
        if (typeof requestAnimationFrame !== "undefined") {
          requestAnimationFrame(processBatch);
        } else {
          setTimeout(processBatch, 0);
        }
      } else {
        resolve(regions);
      }
    };

    // Start processing
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(processBatch);
    } else {
      setTimeout(processBatch, 0);
    }
  });
}

export async function computeBrickLayerCounts(
  pointData: any,
  layerGeoJSON: any,
  layerType: "municipalities" | "countries" | "timezones"
): Promise<RegionData[]> {
  if (!pointData?.features || !layerGeoJSON?.features) {
    return [];
  }

  // Extract point features - use ALL points, don't filter
  const pointFeatures =
    pointData.features.filter((f: any) => f.geometry?.type === "Point") || [];

  if (pointFeatures.length === 0 || layerGeoJSON.features.length === 0) {
    return [];
  }

  // Check cache first
  const cacheKey = getCacheKey(pointData, layerGeoJSON, layerType);
  const cached = computationCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Pre-compute bounding boxes for all features to optimize point-in-polygon checks
  const featureBBoxes = layerGeoJSON.features.map((feature: GeoJSON.Feature) => {
    return featureBoundingBox(feature);
  });

  // Process regions in batches to avoid blocking UI
  // Use smaller batch size for large datasets
  const batchSize = pointFeatures.length > 5000 ? 5 : 10;
  const regions = await processRegionsInBatches(
    layerGeoJSON.features,
    pointFeatures,
    featureBBoxes,
    layerType,
    batchSize
  );

  // Cache the result (with size limit)
  if (computationCache.size >= CACHE_SIZE_LIMIT) {
    // Remove oldest entry (simple FIFO)
    const firstKey = computationCache.keys().next().value;
    if (firstKey !== undefined) {
      computationCache.delete(firstKey);
    }
  }
  computationCache.set(cacheKey, regions);

  return regions;
}

